/**
 * Capacitor Initialization Module
 *
 * Centraliza toda a configuracao de plugins nativos do Capacitor.
 * No web, todas as chamadas sao no-ops gracefull.
 */
import { Capacitor } from "@capacitor/core";

// ============================================
// Lazy imports - so carrega no native
// ============================================
let _App: typeof import("@capacitor/app").App | null = null;
let _StatusBar: typeof import("@capacitor/status-bar").StatusBar | null = null;
let _SplashScreen: typeof import("@capacitor/splash-screen").SplashScreen | null = null;
let _Network: typeof import("@capacitor/network").Network | null = null;
let _ScreenOrientation: typeof import("@capacitor/screen-orientation").ScreenOrientation | null = null;

// Network status state
type NetworkListener = (connected: boolean) => void;
let networkListeners: NetworkListener[] = [];
let isConnected = true;

/**
 * Retorna se o app esta rodando no Capacitor nativo.
 */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Retorna o estado atual da conexao de rede.
 */
export function getNetworkStatus(): boolean {
  return isConnected;
}

/**
 * Registra listener para mudancas de status de rede.
 * Retorna funcao de cleanup.
 */
export function onNetworkChange(listener: NetworkListener): () => void {
  networkListeners.push(listener);
  return () => {
    networkListeners = networkListeners.filter(l => l !== listener);
  };
}

/**
 * Inicializa todos os plugins do Capacitor.
 * Deve ser chamado uma vez no App.tsx.
 * No web, eh um no-op.
 */
export async function initCapacitor(): Promise<void> {
  if (!isNative()) {
    // No web, apenas configura o listener de rede via API nativa do browser
    setupWebNetworkDetection();
    return;
  }

  console.log("[Capacitor] Initializing native plugins...");

  try {
    await Promise.all([
      initStatusBar(),
      initSplashScreen(),
      initBackButton(),
      initNetwork(),
      initScreenOrientation(),
    ]);

    console.log("[Capacitor] All plugins initialized");

    // Hide native splash as soon as plugins are ready
    // Custom React splash (if any) will remain on screen until user interaction
    setTimeout(() => {
      hideSplashScreen();
    }, 300);
  } catch (error) {
    console.error("[Capacitor] Plugin initialization error:", error);
  }
}

// ============================================
// StatusBar
// ============================================
async function initStatusBar(): Promise<void> {
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    _StatusBar = StatusBar;

    // Alinhado com capacitor.config.ts (overlaysWebView: false): a barra fica
    // FORA do WebView — nenhum HUD nasce escondido sob a status bar/notch,
    // mesmo sem env(safe-area-inset-*) propagado pelo WebView Android.
    // (Antes este init forçava overlay: true em runtime, contradizendo o
    // config; o modo imersivo real acontece via hideStatusBar() no gameplay.)
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#0a0a1a" });

    console.log("[Capacitor] StatusBar configured");
  } catch (error) {
    console.warn("[Capacitor] StatusBar not available:", error);
  }
}

/**
 * Esconde a StatusBar (durante gameplay).
 */
export async function hideStatusBar(): Promise<void> {
  if (!_StatusBar) return;
  try {
    await _StatusBar.hide();
  } catch {}
}

/**
 * Mostra a StatusBar (no menu).
 */
export async function showStatusBar(): Promise<void> {
  if (!_StatusBar) return;
  try {
    await _StatusBar.show();
  } catch {}
}

// ============================================
// SplashScreen
// ============================================
async function initSplashScreen(): Promise<void> {
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    _SplashScreen = SplashScreen;

    // O SplashScreen nativo eh controlado pelo capacitor.config.ts
    // Aqui apenas garantimos que ele sera escondido apos o app carregar
    // O hide sera chamado pelo App.tsx quando o splash interativo for mostrado
    console.log("[Capacitor] SplashScreen ready");
  } catch (error) {
    console.warn("[Capacitor] SplashScreen not available:", error);
  }
}

/**
 * Esconde o SplashScreen nativo do Capacitor.
 * Chamar quando o app terminar de carregar.
 */
export async function hideSplashScreen(): Promise<void> {
  if (!_SplashScreen) return;
  try {
    await _SplashScreen.hide();
  } catch {}
}

// ============================================
// Back Button (Android)
// ============================================
async function initBackButton(): Promise<void> {
  try {
    const { App } = await import("@capacitor/app");
    _App = App;

    App.addListener("backButton", ({ canGoBack }) => {
      // Dispara evento customizado para o React tratar
      window.dispatchEvent(new CustomEvent("capacitor:backButton", {
        detail: { canGoBack },
      }));
    });

    // Tratar app lifecycle (pause/resume)
    App.addListener("appStateChange", ({ isActive }) => {
      window.dispatchEvent(new CustomEvent("capacitor:appStateChange", {
        detail: { isActive },
      }));

      if (isActive) {
        console.log("[Capacitor] App resumed");
      } else {
        console.log("[Capacitor] App paused (background)");
      }
    });

    // URL open (deep links)
    App.addListener("appUrlOpen", ({ url }) => {
      window.dispatchEvent(new CustomEvent("capacitor:urlOpen", {
        detail: { url },
      }));
      console.log("[Capacitor] URL opened:", url);
    });

    console.log("[Capacitor] BackButton & Lifecycle configured");
  } catch (error) {
    console.warn("[Capacitor] App plugin not available:", error);
  }
}

/**
 * Sai do app (Android).
 */
export async function exitApp(): Promise<void> {
  if (!_App) return;
  try {
    await _App.exitApp();
  } catch {}
}

// ============================================
// Network Detection
// ============================================
async function initNetwork(): Promise<void> {
  try {
    const { Network } = await import("@capacitor/network");
    _Network = Network;

    // Verificar status inicial
    const status = await Network.getStatus();
    isConnected = status.connected;

    // Escutar mudancas
    Network.addListener("networkStatusChange", (status) => {
      const wasConnected = isConnected;
      isConnected = status.connected;

      console.log("[Capacitor] Network:", status.connected ? "online" : "offline",
        `(${status.connectionType})`);

      // Notificar listeners
      networkListeners.forEach(l => l(status.connected));

      // Disparar evento para o React
      window.dispatchEvent(new CustomEvent("capacitor:networkChange", {
        detail: {
          connected: status.connected,
          connectionType: status.connectionType,
          wasConnected,
        },
      }));
    });

    console.log("[Capacitor] Network detection active:", status.connected ? "online" : "offline");
  } catch (error) {
    console.warn("[Capacitor] Network plugin not available:", error);
  }
}

/**
 * Setup basico de deteccao de rede para web (fallback).
 */
function setupWebNetworkDetection(): void {
  isConnected = navigator.onLine;

  window.addEventListener("online", () => {
    isConnected = true;
    networkListeners.forEach(l => l(true));
    window.dispatchEvent(new CustomEvent("capacitor:networkChange", {
      detail: { connected: true, connectionType: "unknown", wasConnected: false },
    }));
  });

  window.addEventListener("offline", () => {
    isConnected = false;
    networkListeners.forEach(l => l(false));
    window.dispatchEvent(new CustomEvent("capacitor:networkChange", {
      detail: { connected: false, connectionType: "none", wasConnected: true },
    }));
  });
}

// ============================================
// Screen Orientation
// ============================================
async function initScreenOrientation(): Promise<void> {
  try {
    const { ScreenOrientation } = await import("@capacitor/screen-orientation");
    _ScreenOrientation = ScreenOrientation;

    // Livre, como no navegador. Este init travava portrait e, com isso,
    // desligava no app todo o layout deitado (variante Tailwind `short:`)
    // que o jogo já tem para hub, HUD e telas de fim de partida.
    // Quem quiser prender uma tela específica chama lockPortrait/lockLandscape.
    await ScreenOrientation.unlock();

    console.log("[Capacitor] Screen orientation unlocked (segue o device)");
  } catch (error) {
    console.warn("[Capacitor] ScreenOrientation not available:", error);
  }
}

/**
 * Tranca orientacao em portrait.
 */
export async function lockPortrait(): Promise<void> {
  if (!_ScreenOrientation) return;
  try {
    await _ScreenOrientation.lock({ orientation: "portrait" });
  } catch {}
}

/**
 * Tranca orientacao em landscape.
 */
export async function lockLandscape(): Promise<void> {
  if (!_ScreenOrientation) return;
  try {
    await _ScreenOrientation.lock({ orientation: "landscape" });
  } catch {}
}

/**
 * Libera orientacao (permite rotacao).
 */
export async function unlockOrientation(): Promise<void> {
  if (!_ScreenOrientation) return;
  try {
    await _ScreenOrientation.unlock();
  } catch {}
}

// ============================================
// Sinais de idade das app stores — Lei Felca (arts. 12-13)
// ============================================

/**
 * Consome o sinal de idade fornecido pela app store (Google Play / App Store)
 * e envia ao servidor, que o registra APENAS na direção protetiva (o servidor
 * rejeita autopromoção a "adulto" vinda do cliente — art. 9º).
 *
 * Quem chama: `syncPlatformAgeSignal()` em `lib/ageSignals.ts`, depois de
 * consultar a Play Age Signals API pelo plugin local
 * (`android/…/AgeSignalsPlugin.java` — a API não tem plugin Capacitor
 * publicado, então ele foi escrito dentro do projeto Android).
 *
 * A assinatura não aceita "adult" de propósito: o servidor recusaria (art. 9º).
 * Sem sinal, ele já trata a conta como potencialmente de menor — a ausência
 * nunca ENFRAQUECE a proteção.
 */
export async function reportPlatformAgeSignal(
  bracket: "under13" | "13_15" | "16_17",
): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { apiFetch } = await import("./apiBase");
    const { getAuthHeaders } = await import("./stores/useAuth");
    const source = Capacitor.getPlatform() === "ios" ? "appstore_apple" : "appstore_google";
    const response = await apiFetch("/api/protection/age-signal", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ source, bracket }),
    });
    return response.ok;
  } catch (error) {
    console.warn("[Capacitor] Falha ao reportar sinal de idade:", error);
    return false;
  }
}
